/**
 * Copyright (c) 2025 George Nassef, Vector Engineering LLC
 * All rights reserved.
 * 
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 * Commercial use requires explicit written permission.
 */

import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  CardActions, 
  Button, 
  CircularProgress,
  Divider,
  Alert,
  Paper,
  IconButton
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import HomeIcon from '@mui/icons-material/Home';
import RefreshIcon from '@mui/icons-material/Refresh';
import apiService, { Script, Character } from '../services/api';
import { LinearProgress, Chip } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CreateIcon from '@mui/icons-material/Create';
import TimerIcon from '@mui/icons-material/Timer';
import { estimateScriptDuration, formatDuration } from '../utils/durationEstimator';

// Extend the script interface to include the new fields
interface EnhancedScript extends Script {
  status?: 'draft' | 'in-progress' | 'completed';
  last_step?: number;
  is_completed?: boolean;
  updated_at?: string;
  project_id?: number;
}

// Define a status type for the script
type ScriptStatus = 'draft' | 'in-progress' | 'completed';

const ScriptsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [scripts, setScripts] = useState<EnhancedScript[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  // Force a refresh of the scripts list
  const refreshScripts = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  useEffect(() => {
    const fetchScripts = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Clear any stored form data when returning to the scripts list
        // to prevent stale data when creating a new script
        sessionStorage.removeItem('tiktokDrama_formData');
        
        const scriptData = await apiService.getScripts();

        // Check if we got empty array back
        if (Array.isArray(scriptData) && scriptData.length === 0) {
          setScripts([]);
        }
        // Check for data format issues
        else if (!Array.isArray(scriptData)) {
          console.error('Invalid script data format:', scriptData);
          setError('The server returned an invalid data format.');
          setScripts([]);
        }
        // Process the scripts
        else {
          // Check if we have completion status or derive it 
          const processedScripts: EnhancedScript[] = scriptData.map(script => {
            // Determine script status based on content and is_completed flag
            const status: ScriptStatus = script.is_completed ? 'completed' : 
                          script.content ? 'in-progress' : 'draft';
            
            // Create the enhanced script with proper typing
            return {
              ...script,
              status,
              // Add a default updated_at if not present
              updated_at: (script as any).updated_at || script.created_at
            };
          });
          
          // Sort by most recently updated first
          processedScripts.sort((a, b) => 
            new Date(b.updated_at || b.created_at).getTime() - 
            new Date(a.updated_at || a.created_at).getTime()
          );
          
          setScripts(processedScripts);
        }
      } catch (err) {
        console.error('Error fetching scripts:', err);
        setError('Failed to load scripts. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchScripts();
  }, [refreshTrigger]); // Reload when refresh is triggered

  const handleEditScript = (scriptId: string) => {
    console.log(`Navigating to edit script: ${scriptId}`);
    
    // Check if this is a UUID or a project-based ID
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(scriptId);
    
    if (isUuid) {
      // For UUID scripts (from direct generation), store the ID in session storage
      // so we can recover it if needed
      sessionStorage.setItem('last_script_id', scriptId);
    }
    
    navigate(`/scripts/${scriptId}`);
  };

  const handleCreateVideo = (scriptId: string) => {
    // Show alert instead of navigating since video generation is disabled
    alert('Video generation is not available in the current version.');
  };

  const handleCreateNewScript = () => {
    // Clear any previous form data when creating a new script
    sessionStorage.removeItem('tiktokDrama_formData');
    sessionStorage.removeItem('tiktokDrama_currentProject');
    
    navigate('/create-script');
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  // Helper function to truncate text
  const truncateText = (text: string, maxLength: number = 150) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Helper function to extract first scene from script content
  const extractFirstScene = (content: string) => {
    if (!content) return '';
    
    const lines = content.split('\n').filter(line => line.trim());
    
    // Try to find first 5-10 lines of dialogue
    const dialogueLines = lines.filter(line => line.includes(':'));
    if (dialogueLines.length > 0) {
      return dialogueLines.slice(0, Math.min(6, dialogueLines.length)).join('\n');
    }
    
    // Fallback to first few lines
    return lines.slice(0, Math.min(6, lines.length)).join('\n');
  };

  if (isLoading) {
    return (
      <Box className="flex justify-center items-center" style={{ minHeight: '300px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="p-4">
        <Alert severity="error" className="mb-4">
          {error}
        </Alert>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            onClick={refreshScripts}
            startIcon={<RefreshIcon />}
          >
            Try Again
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateNewScript}
          >
            Create New Script
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box className="flex justify-between items-center mb-6">
        <Box display="flex" alignItems="center">
          <IconButton 
            onClick={handleBackToHome}
            aria-label="back to home"
            sx={{ mr: 1 }}
          >
            <HomeIcon />
          </IconButton>
          <Typography variant="h4" component="h1" className="teen-heading">
            My Scripts
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            onClick={refreshScripts}
            startIcon={<RefreshIcon />}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateNewScript}
            className="teen-button-primary"
          >
            Create New Script
          </Button>
        </Box>
      </Box>

      {scripts.length === 0 ? (
        <Paper className="p-6 text-center">
          <Typography variant="h6" gutterBottom>
            You don't have any scripts yet
          </Typography>
          <Typography variant="body1" paragraph className="mb-4">
            Get started by creating your first script!
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateNewScript}
            className="teen-button-primary"
          >
            Create Your First Script
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {scripts.map((script) => (
            <Grid item xs={12} key={script.id}>
              <Card className="flex flex-col">
                <CardContent className="flex-grow">
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6" className="teen-subheading">
                      {script.title || "Untitled Script"}
                    </Typography>
                    {script.status && (
                      <Chip 
                        size="small" 
                        label={script.status.charAt(0).toUpperCase() + script.status.slice(1)}
                        color={script.status === 'completed' ? 'success' : 
                               script.status === 'in-progress' ? 'primary' : 'default'}
                        icon={script.status === 'completed' ? <AssignmentTurnedInIcon /> : 
                              script.status === 'in-progress' ? <CreateIcon /> : <AssignmentIcon />}
                      />
                    )}
                  </Box>
                  
                  <Typography variant="caption" color="text.secondary" className="mb-2 block">
                    Created: {new Date(script.created_at).toLocaleString()}
                    {script.updated_at && script.updated_at !== script.created_at && 
                      ` (Updated: ${new Date(script.updated_at).toLocaleString()})`}
                  </Typography>
                  
                  {/* Progress bar for in-progress scripts */}
                  {script.status === 'in-progress' && script.last_step !== undefined && (
                    <Box my={1}>
                      <Typography variant="caption" display="block" gutterBottom>
                        Progress: Step {script.last_step + 1}/5
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={((script.last_step + 1) / 5) * 100} 
                        sx={{ height: 8, borderRadius: 5 }}
                      />
                    </Box>
                  )}
                  
                  <Divider className="my-2" />
                  
                  <Typography variant="body2" className="text-gray-600 mb-2">
                    <strong>Setting:</strong> {script.scene_setting || "Not specified"}
                  </Typography>
                  
                  <Typography variant="body2" className="text-gray-600 mb-2">
                    <strong>Characters:</strong> {
                      script.characters && Object.keys(script.characters).length > 0 
                        ? Object.keys(script.characters).join(', ') 
                        : "None specified"
                    }
                  </Typography>

                  <Typography variant="body2" className="text-gray-600 mb-2">
                    <Box display="flex" alignItems="center">
                      <TimerIcon fontSize="small" sx={{ mr: 1 }} />
                      <strong>Duration:</strong> {
                        script.content 
                          ? formatDuration(estimateScriptDuration(
                              script.content.split(/\s+/).length,
                              (script.content.match(/SCENE \d+/g) || []).length || 5
                            ))
                          : "TBD"
                      }
                    </Box>
                  </Typography>
                  
                  <Divider className="my-2" />
                  
                  {script.content ? (
                    <Box className="bg-gray-50 dark:bg-gray-800 p-2 rounded my-2 font-mono text-sm whitespace-pre-wrap">
                      {truncateText(extractFirstScene(script.content))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                      Script content not generated yet
                    </Typography>
                  )}
                </CardContent>
                <CardActions className="bg-gray-50 dark:bg-gray-800">
                  <Button 
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditScript(script.id)}
                  >
                    Edit
                  </Button>
                  <Button 
                    size="small"
                    startIcon={<VideoLibraryIcon />}
                    onClick={() => handleCreateVideo(script.id)}
                    className="ml-2"
                  >
                    Create Video
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default ScriptsPage;
